import { useState } from 'react'
import { useWorkerSummaries, useWorkerSummary } from '../hooks.js'
import { formatWhen, spanLabel } from '../labels.js'
import { PerformanceHighlights } from './PerformanceHighlights.js'
import { HelpIcon, LoadFailure, Skeleton } from '../../../modules/core/index.js'

/**
 * The worker's own Performance tab: the summaries the facility has
 * released to them, the latest shown in bold strokes and any earlier one a
 * tap away. The shell lists the tab only while the add-on is on at the
 * worker's facility, so nothing is asked for otherwise. Nothing says that
 * a summary exists unreleased: the API answers only the released ones, and
 * with none the page says only that nothing has been shared yet.
 */
export function WorkerPerformancePage({ workerId }: { workerId: string }) {
  const summaries = useWorkerSummaries(workerId)
  const [chosenId, setChosenId] = useState<string | null>(null)
  const released = summaries.data ?? []
  const shown = released.find((item) => item.summaryId === chosenId) ?? released[0] ?? null
  const summary = useWorkerSummary(workerId, shown?.summaryId ?? null)

  if (summaries.isPending || (shown !== null && summary.isPending)) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (summaries.isError || summary.isError) {
    return <LoadFailure className="p-8" message="Your performance summary could not be loaded." onRetry={() => void Promise.all([summaries.refetch(), summary.refetch()])} retrying={summaries.isFetching || summary.isFetching} />
  }
  if (!shown || !summary.data) {
    return (
      <div className="max-w-md mx-auto p-4">
        <div className="bg-white border border-marine/15 p-5 text-center">
          <div className="text-sm font-extrabold text-ink">Nothing shared yet</div>
          <p className="mt-1 text-[11px] leading-relaxed text-ink-mid">When your facility shares a performance summary with you, it appears here.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto flex flex-col gap-4 p-4">
      <section aria-label="Your performance summary">
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Your performance summary<HelpIcon topic="performance_reviews.worker" /></div>
          <div className="text-[10px] text-ink-mid">{spanLabel(shown.span)}</div>
        </div>
        <PerformanceHighlights summary={summary.data} compact />
        <p className="mt-1.5 text-[10px] leading-relaxed text-ink-mid">
          Shared with you by your facility{shown.release ? ` on ${formatWhen(shown.release.releasedAt)}` : ''}. {summary.data.provenance.statement}
        </p>
      </section>

      {released.length > 1 && (
        <section aria-label="Shared summaries">
          <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Shared with you</div>
          <ul className="flex flex-col gap-1.5">
            {released.map((item) => {
              const isShown = item.summaryId === shown.summaryId
              return (
                <li key={item.summaryId}>
                  <button
                    type="button"
                    onClick={() => setChosenId(item.summaryId)}
                    aria-current={isShown ? 'true' : undefined}
                    className={`flex w-full items-baseline justify-between gap-3 border p-2.5 text-left text-[11px] ${isShown ? 'border-marine bg-marine-lt/40 font-bold text-ink' : 'border-marine/15 bg-white text-ink hover:border-marine/40'}`}
                  >
                    <span>{spanLabel(item.span)}</span>
                    {item.release && <span className="text-[10px] font-normal text-ink-mid">shared {formatWhen(item.release.releasedAt)}</span>}
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}
